import { Alert, Col, Container, Row, Spinner } from "react-bootstrap"
import { useRef, useState, useEffect } from "react"
import { t } from "../locale"
import {
    useCamera,
    saveVideo,
    createJob,
    showKAlert,
    disableClickables,
    checkVideo,
    checkPermission
} from "../helpers/helpers";

import 'bootstrap-icons/font/bootstrap-icons.css';
import { Offcanvas } from 'bootstrap';
import { useNavigate } from "react-router-dom";
import StepGuide from "../components/StepGuide";
import VerificationStepNav from "../components/VerificationStepNav";

function Index() {
    const navigate = useNavigate();

    const [hasAccess, setHasAccess] = useState(true)
    const [expiredPage, setExpiredPage] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [recordingLabel, setRecordingLabel] = useState('')
    const [labelColor, setLabelColor] = useState('text-primary')
    const [label, setLabel] = useState(false)
    const [startRecord, setStartRecord] = useState(true)
    const [stopRecord, setStopRecord] = useState(false)
    const [nextButton, setNextButton] = useState(false)
    const [buttonDisabled, setButtonDisabled] = useState('')
    const [spaceHeight, setSpaceHeight] = useState('mt-5')
    const fileInputRefVideo = useRef<HTMLInputElement | null>(null)
    const [referenceNumber, setReferenceNumber] = useState('')
    const [videoButtonLabel, setVideoButtonLabel] = useState('')
    const [navigateUrl, setNavigateUrl] = useState('')
    const [requestData, setRequestdata] = useState(null)
    const [loading, setLoading] = useState(false);

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const recordedChunks = useRef<Blob[]>([]);

    const pathSegments = window.location.pathname.split("/");
    const companyId = pathSegments[1];
    const requestId = pathSegments[2];

    const { mobileNav, desktopNav } = VerificationStepNav({
        currentStep: 4,
        companyId,
        requestId,
        onBack: undefined,
        spaceHeight
      });

    useEffect(() => {
        checkPermission()
        fetchData()
        handleDeviceDetection()

        const offcanvas = document.getElementById('offcanvasRight')
        if (offcanvas) {
            new Offcanvas(offcanvas).show()
        }
    }, [])

    useCamera('environment', isMobile);

    const handleDeviceDetection = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        const isMobile = /iphone|ipad|ipod|android|blackberry|windows phone/g.test(userAgent);
        const isTablet = /(ipad|tablet|playbook|silk)|(android(?!.*mobile))/g.test(userAgent);

        if (isMobile) {
            setIsMobile(true)
            setSpaceHeight('')
            setVideoButtonLabel(t('videoUploadMobile'))
        } else if (isTablet) {
            setIsMobile(true)
            setVideoButtonLabel(t('videoUploadMobile'))
        } else {
            setVideoButtonLabel(t('videoUpload'))
        }
    }

    const handleButtonVideoUpload = () => {
        if (fileInputRefVideo.current) {
            fileInputRefVideo.current.click();
        }
    };

    const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('video/')) {
            showKAlert('warning', t('pleaseUploadValidVideo'));
            return;
        }

        setLoading(true)
        const form = new FormData();
        form.append('video', file);
        form.append('companyId', companyId);
        form.append('requestId', requestId);

        try {
            const r = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/video`, { method: 'POST', body: form });
            if (!r.ok) throw new Error(String(r.status));
            showKAlert('success', t('success'));
            setLoading(false)
            setRecordingLabel('Success');
            setLabel(true);
            setNextButton(true);
        } catch (err) {
            console.error(err);
            showKAlert('danger', t('videoNotUploaded'));
        } finally {
            event.target.value = '';
        }
    };

    const fetchData = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v1/request/data/all`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        companyId: companyId,
                        requestId: requestId,
                    }),
                }
            );
            if (!response.ok) {
                throw new Error("Failed to fetch data");
            }

            const data = await response.json();
            setRequestdata(data)

            if (data) {
                if (data.status === 'EXPIRED') setExpiredPage(false);
            }

            if (data.fintsCheck) {
                setNavigateUrl(`/${companyId}/${requestId}/si/payment/p`)
            } else {
                setNavigateUrl(`/success`)
            }


        } catch (error) {
            console.error("Error fetching data:", error);
            setHasAccess(false);
        }
    };

    const startRecording = async () => {
        try {
            setStartRecord(false)
            setStopRecord(true)
            setRecordingLabel(t('recording'))
            setLabel(true)
            setButtonDisabled('disabled')
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })

            if (videoRef.current) {
                videoRef.current.srcObject = stream
            }

            const mediaRecorder = new MediaRecorder(stream, { mimeType: "video/webm;codecs=vp8,opus" })
            mediaRecorderRef.current = mediaRecorder

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    recordedChunks.current.push(event.data)
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(recordedChunks.current, { type: "video/webm" })
                if (blob.size > 0) {
                    setLoading(true)
                    saveVideo(blob, companyId, requestId, 'webm')
                        .then((r) => {
                            if (!r) {
                                showKAlert('danger', t('videoNotUploaded'))
                            } else {
                                setNextButton(true)
                            }
                        })
                        .catch((err) => console.log(err))
                        .finally(() => { setLoading(false) })

                } else {
                    showKAlert('danger', t('videoFileEmpty'))
                }
                recordedChunks.current = []
            };

            mediaRecorder.start()
        } catch (error) {
            console.error("Error starting video recording:", error)
        }
    };

    const stopRecording = () => {
        setStartRecord(true)
        setStopRecord(false)
        setRecordingLabel(t('success'))
        setLabelColor('text-success')

        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
        }

        if (videoRef.current && videoRef.current.srcObject) {
            const stream = videoRef.current.srcObject as MediaStream;
            stream.getTracks().forEach((track) => track.stop());
        }
    };

    const next = () => {
        checkVideo(companyId, requestId)
            .then((r) => {
                if (r.status) {
                    navigate(navigateUrl)
                    if (requestData && requestData['fintsCheck']) {
                        createJob(companyId, requestId, setReferenceNumber)
                    }
                } else {
                    showKAlert('danger', t('videoNotSaved'))
                    disableClickables()
                }
            })
    }

    return (
        <>
            {mobileNav}
            {hasAccess ? (
                <Container hidden={expiredPage} className={`overflow-hidden ${spaceHeight}`}>
                    <Row className="row overflow-auto">
                        {desktopNav}
                        <Col className={`d-flex flex-column h-sm-100 px-sm-5 ${spaceHeight}`}>
                            <Row className="mt-1 mb-4">
                                <div className="d-flex justify-content-end">
                                    <button className="btn btn-sm btn-outline-secondary rounded-pill" type="button"
                                        data-bs-toggle="offcanvas" data-bs-target="#offcanvasRight" aria-controls="offcanvasRight">{t('instructions')}
                                    </button>
                                </div>
                            </Row>
                            <main className="row overflow-auto px-sm-5 position-relative">

                                <div className="col pt-4 pt-sm-5 text-center text-sm-start mt-n1">
                                    <h4 className="mb-4">{t('videoTitle')}</h4>
                                    <div className="row mt-6 mt-sm-0 ">
                                        <h5 className="">{t('videoSubTitle')}</h5>
                                        {
                                            !isMobile && <div className="col-sm-7 text-center mx-auto mx-sm-0">
                                                {/* <!-- This is image holder after upload or for web camera --> */}
                                                <div className="rounded-3 border border-primary border-3 video-frame">
                                                    <video className="h-100 w-100 video-element " id="videoElement" autoPlay muted></video>

                                                </div>
                                                <Row>
                                                    {(label && <span className={`text-primary h5 ${labelColor}`}>{recordingLabel}</span>)}
                                                </Row>
                                            </div>
                                        }
                                        <div className='col-sm-4 text-center mt-2 align-content-center'>
                                            <StepGuide
                                                step={1}
                                                text={t('guideStepVideo')}
                                                show={startRecord && !isMobile}
                                                arrowAlign='center'
                                            />
                                            {(startRecord &&
                                                <button className={`btn btn-primary rounded-pill mt-2 d-none d-sm-inline`}
                                                    onClick={startRecording}
                                                    style={{ textTransform: 'none' }}>{t('videoRecord')}</button>
                                            )}
                                            {(stopRecord && <button className={`btn btn-primary rounded-pill mt-2 d-none d-sm-inline `}
                                                onClick={stopRecording}>{t('videoStop')}</button>)}
                                            <p className="my-2 d-none d-sm-block fs-5">{t('or')}</p>
                                            <StepGuide
                                                step={isMobile ? 1 : 2}
                                                text={t('guideStepVideoUpload')}
                                                show={startRecord}
                                                arrowAlign='center'
                                            />
                                            <input
                                                type="file"
                                                accept="video/*"
                                                onChange={handleVideoUpload}
                                                ref={fileInputRefVideo}
                                                style={{ display: 'none' }}
                                            />
                                            <button className={`btn btn-primary rounded-pill mt-2 mt-sm-0 ${buttonDisabled}`}
                                                onClick={handleButtonVideoUpload}
                                                style={{ textTransform: 'none' }}>{videoButtonLabel}</button>
                                            <hr className="my-3" />
                                            {(nextButton && <button
                                                className="btn btn-success btn-lg text-white"
                                                type="submit" onClick={next}>{t('nextStep')} »</button>
                                            )}
                                        </div>
                                    </div>
                                    <hr className="my-5 mb-3" />
                                    <p className="my-5">&nbsp;</p>

                                </div>
                                <div className="offcanvas offcanvas-end" id="offcanvasRight"
                                    aria-labelledby="offcanvasRightLabel">
                                    <div className="offcanvas-header">
                                        <h5 id="offcanvasRightLabel">{t('instructions')}</h5>
                                        <button type="button" className="btn btn-close text-reset" data-bs-dismiss="offcanvas"
                                            aria-label="Close"></button>
                                    </div>
                                    <div className="offcanvas-body">
                                        <h5 className="mb-4">{t('videoInstructionsTitle')}</h5>
                                        <ol className="mb-4">
                                            <li>{t('videoInstructionsFirst')}</li>
                                            <li>{t('videoInstructionsSecond')}</li>
                                            <li>{t('videoInstructionsThird')}</li>
                                            <li>{t('videoInstructionsFourth')}</li>
                                        </ol>
                                        <p className="mb-2">{t('videoExample')}</p>
                                        <img src="/images/video_id.png" className="img-fluid mt-2" />
                                    </div>
                                </div>
                            </main>
                        </Col>
                    </Row>
                </Container>
            ) : (
                <Alert variant="danger" className="mt-3">
                    {t("accessDenied")}
                </Alert>
            )}
            <div className="py-2" hidden={!expiredPage}>
                <h4>{t("requestExpired")}</h4>
                <p>{t("accessExpired")}</p>
            </div>
            {loading && (
                <div className="d-flex justify-content-center align-items-center position-fixed top-0 start-0 w-100 h-100 bg-white bg-opacity-75" style={{ zIndex: 9999 }}>
                    <Spinner animation="border" variant="primary" role="status" />
                    <span className="ms-2">{t('Loading...')}</span>
                </div>
            )}
        </>
    )
}

export default Index;